import {combineLatest, Observable, of, ReplaySubject, Subject} from 'rxjs';
import {
    TimeFilter,
    TimeFilterComponent,
    TimeFilterProfile,
    TimeFilterType
} from '../../shared/time-filter/time-filter.component';
import {SmagApiService} from '../../api/smag-api.service';
import {Inject} from '@angular/core';
import {APP_CONFIG_TOKEN, AppConfig} from '../../app.config';
import {ResizeService} from '../../shared/resize.service';
import {ChartLine} from '../../shared/line-chart/line-chart.component';
import {NiceValuePipe} from '../../shared/shared.pipe';
import {I18NTimestampPipe} from '../../i18n/i18n.pipe';
import {I18NService} from '../../i18n/i18n.service';
import {AuthService} from 'auth-commons';
import {map, switchMap, takeUntil} from 'rxjs/operators';
import {Entity} from 'smag-commons/cfg/hierarchy';
import {CdkDragDrop, moveItemInArray} from '@angular/cdk/drag-drop';
import {MeasureType as MeteoMeasureType} from 'smag-commons/cfg/connector/meteo';
import {SharedConfig} from '../../shared/shared.config';
import {TIME_DELTA_QUARTER_HOUR} from '../../shared/line-chart/line-chart.pipe';
import {Measure} from 'smag-commons/dto/msg';
import * as _ from 'lodash';
import {isDefined} from '../../commons/commons';

export interface ResourceSelection {
    resourceId: string;
}

export type DataFactory = (timeRange: [number, number]) => Observable<ForecastMeasureData>;

export enum ForecastMeasureMode {
    SINGLE, // only the most recent [acquire timestamp] measure is shown for each data timestamp
    MULTIPLE // all measures are shown (grouped by acquire timestamp)
}

export interface AcquiredMeasure {
    acquireTimestamp: number;
    data: [number, number][]; // [data timestamp, value]
}

interface BaseForecastMeasure {
    name: string;
}

export interface ForecastMeasureOption extends BaseForecastMeasure {
    dataFactory: DataFactory;
}

export interface ForecastMeasure extends BaseForecastMeasure {
    data: Observable<ForecastMeasureData>;
}

export interface ForecastMeasureData {
    data: ChartLine[];
    uom?: string;
}

export interface ForecastMeasureSettings {
    name: string;
    color?: string;
    defaultUom?: string;
}

interface ForecastMeasureResource extends Entity {
    id: string;
}

const DEFAULT_TIME_FILTER: TimeFilterType = TimeFilterType.NEXT_24_HOURS;

export abstract class WeatherForecastComponent {

    private resourceSelectionSubj: Subject<ResourceSelection>;

    private selectionSubj: Subject<ForecastMeasureOption[]>;
    private timeFilter: Subject<TimeFilter>; // currently selected time filter

    protected destroyed = new Subject<void>();
    protected forecastMeasureSettings: { [meas in MeteoMeasureType]: ForecastMeasureSettings };
    protected nvp: NiceValuePipe;
    protected tsp: I18NTimestampPipe;

    contentResize: Observable<void>;
    resource: ForecastMeasureResource | null; // currently selected resource
    mode: ForecastMeasureMode;
    options: ForecastMeasureOption[];
    selection: ForecastMeasureOption[];
    measures: ForecastMeasure[];

    // exported in HTML
    TimeFilterProfile = TimeFilterProfile;
    ForecastMeasureMode = ForecastMeasureMode;
    DEFAULT_TIME_FILTER = DEFAULT_TIME_FILTER;
    TIME_DELTA_QUARTER_HOUR = TIME_DELTA_QUARTER_HOUR;

    protected constructor(@Inject(APP_CONFIG_TOKEN) protected readonly cfg: AppConfig,
                          protected readonly resizeService: ResizeService,
                          protected readonly i18n: I18NService,
                          protected readonly auth: AuthService,
                          protected readonly apiService: SmagApiService) {
        this.forecastMeasureSettings = mkSettings(cfg.shared);
        this.resource = null;
        this.resourceSelectionSubj = new Subject<ResourceSelection>();
        this.nvp = new NiceValuePipe(i18n);
        this.tsp = new I18NTimestampPipe(i18n, auth);
        this.mode = ForecastMeasureMode.SINGLE;
        this.timeFilter = new ReplaySubject<TimeFilter>(1);
        this.options = [];
        this.measures = [];
        this.selection = [];
        this.selectionSubj = new Subject<ForecastMeasureOption[]>();
    }

    get gridColor(): string {
        return this.cfg.shared.gridColor;
    }

    get tooltipBorderColor(): string {
        return this.cfg.shared.tooltipBorderColor;
    }

    get tooltipBackgroundColor(): string {
        return this.cfg.shared.tooltipBackgroundColor;
    }

    get selectionLineColor(): string {
        return this.cfg.shared.selectionLineColor;
    }

    isOptionSelected(option: ForecastMeasureOption): boolean {
        return this.selection.includes(option); // reference check!
    }

    onSelectResource(resourceId: string) {
        if (resourceId !== this.resource?.id) {
            this.resourceSelectionSubj.next({resourceId});
        }
    }

    onSelectTimeFilter(event: TimeFilter) {
        this.timeFilter.next(event);
    }

    onSelectOption(option: ForecastMeasureOption, selected: boolean) {
        if (selected) {
            this.selection = this.options.filter(opt => this.selection.includes(opt) || opt === option); // reference check!
        } else {
            this.selection = this.selection.filter(opt => opt !== option); // reference check!
        }
        this.selectionSubj.next(this.selection); // triggers reloading
    }

    onMeasureDrop(event: CdkDragDrop<ForecastMeasure[], any>) {
        moveItemInArray(this.measures, event.previousIndex, event.currentIndex);
    }

    onSelectTimeRangeFromChart(event: [number, number]) {
        const [fromTime, toTime] = event;
        if (toTime - fromTime > 3600000) {
            this.timeFilterComponent.setCustomTimeFilter(fromTime, toTime);
        }
    }

    protected abstract get timeFilterComponent(): TimeFilterComponent;

    protected abstract mkMeasureOptions(): Observable<ForecastMeasureOption[]>;

    protected onInit() {
        this.contentResize = this.resizeService.observe(['content']).pipe(
            takeUntil(this.destroyed)
        );

        this.resourceSelectionSubj.pipe(
            switchMap(({resourceId: id}) => id ?
                this.apiService.getEntity(id).pipe(
                    map(res => ({...res, id} as ForecastMeasureResource))
                ) :
                of(null as ForecastMeasureResource)), // ask API for resource details
            takeUntil(this.destroyed)
        ).subscribe(resource => {
            this.resource = resource;
            this.options = [];
            this.selection = this.options; // no options initially available
            this.selectionSubj.next(this.selection);
            this.mkMeasureOptions().subscribe(options => {
                this.options = options;
                this.selection = this.options; // all options initially selected
                this.selectionSubj.next(this.selection);
            });
        });

        combineLatest([
            this.selectionSubj,
            this.timeFilter
        ]).pipe(
            takeUntil(this.destroyed)
        ).subscribe(([selection, timeFilter]) => {
            this.updateMeasures(selection, timeFilter);
        });
    }

    protected onDestroy() {
        this.destroyed.next();
        this.destroyed.complete();
    }

    protected mkMeasureDataFactory(measureType: MeteoMeasureType,
                                   measureProvider: (timeRange: [number, number]) => Observable<Measure[]>,
                                   defaultUom?: string): DataFactory {
        return timeRange => measureProvider(timeRange).pipe(
            switchMap(measures => this.loadMeasuresUom(measures, defaultUom).pipe(
                map(uom => [mkAcquiredMeasures(measures), uom] as [AcquiredMeasure[], string])
            )),
            map(([acquiredMeasures, uom]) => ({
                data: acquiredMeasures.map(meas => ({
                    data: noData(meas.data) ? [] : meas.data,
                    style: {color: this.forecastMeasureSettings[measureType].color, strokeWidth: 1.5},
                    name: this.i18n.translate('Acquired on {0}', this.tsp.transform(meas.acquireTimestamp))
                } as ChartLine)),
                uom
            }))
        );
    }

    private updateMeasures(sel: ForecastMeasureOption[], timeFilter: TimeFilter) {
        const range = timeFilter.range();
        const timeRange: [number, number] = [range.fromTime, range.toTime];
        // try to load new measures' data while at the same time keeping current measure order
        // (possibly changed via drag&drop)
        // TODO: persist measures' order in local storage or user configuration
        const newMeasures = sel.map(opt => mkMeasure(opt, timeRange));
        // this.measures = this.measures
        //     .map(m => newMeasures.find(nm => nm.name === m.name)) // overwrite with same measure in new set
        //     .filter(m => !!m) // keep only measures that exists in new set
        //     .concat(newMeasures
        //         .filter(nm => !this.measures.find(m => m.name === nm.name))); // add new measures that did not exist before
        this.measures = newMeasures;
    }

    private loadMeasuresUom(measures: Measure[], defaultUom?: string): Observable<string> {
        // NOTE: measures are all supposed to have the same `device`
        const device = measures.length ? measures[0].device : null;
        return device ? this.loadDeviceUom(device, defaultUom) : of(defaultUom || null);
    }

    private loadDeviceUom(device: string, defaultUom?: string): Observable<string> {
        return this.apiService.getConnectorDevice(device)
            .pipe(map(d => d.uom || defaultUom || null));
    }
}

function mkAcquiredMeasures(measures: Measure[]): AcquiredMeasure[] {
    // returned measures are sorted by acquire-timestamp in descending order,
    // so that the first entry corresponds to most-recent measures
    return _.sortBy(_.values(_.mapValues(_.groupBy(measures, m => m.acquire_timestamp),
        (mm, acquireTimestamp) => ({
            acquireTimestamp: parseInt(acquireTimestamp),
            data: _.sortBy(mm.map(m => [m.data_timestamp, m.value]), ([t, val]) => t)
        } as AcquiredMeasure))), m => -m.acquireTimestamp);
}

function mkMeasure(option: ForecastMeasureOption, timeRange: [number, number]): ForecastMeasure {
    return {
        name: option.name,
        data: option.dataFactory(timeRange)
    };
}

function mkSettings(cfg: SharedConfig): { [meas in MeteoMeasureType]: ForecastMeasureSettings } {
    return {
        [MeteoMeasureType.TEMPERATURE]: {
            name: 'Temperature',
            color: cfg.colors.temperature,
            defaultUom: '°C'
        },
        [MeteoMeasureType.RADIATION]: {
            name: 'Radiation',
            color: cfg.colors.radiation,
            defaultUom: 'W/m^2'
        },
        [MeteoMeasureType.DEW_POINT]: {
            name: 'Dew point',
            color: cfg.colors.dewPoint,
            defaultUom: '°C'
        },
        [MeteoMeasureType.WIND_CHILL]: {
            name: 'Wind chill',
            color: cfg.colors.windChill,
            defaultUom: '°C'
        },
        [MeteoMeasureType.HEAT_INDEX]: {
            name: 'Heat index',
            color: cfg.colors.heatIndex,
            defaultUom: '°C'
        },
        [MeteoMeasureType.PRESSURE]: {
            name: 'Pressure',
            color: cfg.colors.pressure,
            defaultUom: 'Pa'
        },
        [MeteoMeasureType.HUMIDITY]: {
            name: 'Humidity',
            color: cfg.colors.humidity,
            defaultUom: '%'
        },
        [MeteoMeasureType.RAINFALL]: {
            name: 'Rainfall',
            color: cfg.colors.rainfall,
            defaultUom: 'mm'
        },
        [MeteoMeasureType.UV]: {
            name: 'UV',
            color: cfg.colors.uv,
            defaultUom: 'idx'
        },
        [MeteoMeasureType.LIFTED_INDEX]: {
            name: 'Lifted index',
            color: cfg.colors.liftedIndex,
            defaultUom: 'K'
        },
        [MeteoMeasureType.CLOUD_COVERAGE]: {
            name: 'Cloud coverage',
            color: cfg.colors.cloudCoverage,
            defaultUom: '%'
        },
        [MeteoMeasureType.WIND_SPEED]: {
            name: 'Wind speed',
            color: cfg.colors.windSpeed,
            defaultUom: 'm/s'
        },
        [MeteoMeasureType.WIND_DIR]: {
            name: 'Wind direction',
            color: cfg.colors.windDir,
            defaultUom: 'deg'
        },
        [MeteoMeasureType.WIND_GUSTS]: {
            name: 'Wind gusts',
            color: cfg.colors.windGusts,
            defaultUom: 'm/s'
        },
        [MeteoMeasureType.WEATHER_STATE]: {
            name: 'Overall weather state',
            color: null,
            defaultUom: null
        }
    };
}

function noData(data: [number, number][]): boolean {
    return data.every(([t, d]) => !isDefined(d));
}
